/* eslint-disable no-unused-vars */
import { v4 as uuidv4 } from 'uuid'
import isNil from 'lodash/isNil'
import { getExperimentsStorage } from './experiments-storage'
import { FF } from '@local/do-secundo-feature-flag'

const createVariantBucket = (numVariants, string) => {
  return hashCode(string) % numVariants
}

/**
 * Accepts buckets weights where the first weight corresponds to the weight of the first variant(0 based count) and returns a variant.
 * Example: Input: 2, 1, 1. Variant 0 - 50%, variant 1 - 25%, variant 2 - 25%.
 * @param  {...number} weights
 * @returns {number} Variant
 */

const createWeightedBucket = (...weights) => {
  const buckets = weights.reduce(
    (acc, val, idx) => [...acc, ...Array.from({ length: val }, () => idx)],
    []
  )

  const randomIndex = Math.floor(Math.random() * buckets.length)

  return buckets[randomIndex]
}

/**********************************************
Experiment Name: Guest Feedback
Description: Let 5% of the users submit feedback about the platform
Variants:
  0: Control - No Treatment - 95%
  1: Guest Feedback is visible - 10%
**********************************************/
export const XP_GUEST_FEEDBACK = {
  experimentName: 'xp-guest-feedback',
  flagName: FF.GUEST_FEEDBACK,
  useInitVariant() {
    const ctx = XP_GUEST_FEEDBACK
    const experimentStorage = getExperimentsStorage()
    const storedVariant = experimentStorage.get([ctx.experimentName])
    // allows to manually override variant for testing purposes
    if (!isNil(storedVariant)) {
      return storedVariant
    }
    const dice = Math.random()
    return dice < 0.1 ? 1 : 0
  },
  getTreatment(variant) {
    return variant
  }
}

/************************************************
Experiment Name: Intelligent Upsells
Description: 50% of guests should see intelligent upsells along with their standard upsell treatment
Variants:
  0: Control - No intelligent upsells - 50%
  1: Intelligent Upsells are used when present - 50%
************************************************/
export const XP_INTELLIGENT_UPSELLS = {
  experimentName: 'xp-intelligent-upsells',
  flagName: FF.CART_UPSELLS,
  useInitVariant() {
    const ctx = XP_INTELLIGENT_UPSELLS
    const experimentStorage = getExperimentsStorage()
    const storedVariant = experimentStorage.get([ctx.experimentName])
    // allows to manually override variant for testing purposes
    if (!isNil(storedVariant)) {
      return storedVariant
    }
    // Get a bucket and store it for future use.
    const variant = createVariantBucket(2, Math.random().toString())
    experimentStorage.set(ctx.experimentName, variant)
    return variant
  },
  getTreatment(variant) {
    return variant
  }
}

/************************************************
Experiment Name: Meal Selector
Description: 50% of guests will see Meal Selector Prompt on rx header
Variants:
  0: Control - No meal selector - 50%
  1: Prompt on rx header to open meal selector modal - 50%
************************************************/
export const XP_MEAL_SELECTOR = {
  experimentName: 'xp-meal-selector',
  flagName: FF.MEAL_SELECTOR,
  useInitVariant() {
    const ctx = XP_MEAL_SELECTOR
    const experimentStorage = getExperimentsStorage()
    const storedVariant = experimentStorage.get([ctx.experimentName])
    // allows to manually override variant for testing purposes
    if (!isNil(storedVariant)) {
      return storedVariant
    }
    // Get a bucket and store it for future use.
    const variant = createVariantBucket(2, Math.random().toString())
    experimentStorage.set(ctx.experimentName, variant)
    return variant
  },
  getTreatment(variant) {
    return variant
  }
}

/**********************************************
Experiment Name: Menu items sort
Description: Let 50% of the users see sorted menu items. 
25% will see items with images on top, 
25% items with descriptions on top, 
Variants:
  0: Control - No Treatment - 50%
  1: Items with images are higher in the menu group - 25%
  2: Items with descriptions are higher in the menu group - 25%
**********************************************/
export const XP_MENU_ITEMS_SORT = {
  experimentName: 'xp-menu-item-sort',
  flagName: FF.MENU_ITEM_SORT,
  useInitVariant() {
    const ctx = XP_MENU_ITEMS_SORT
    const experimentStorage = getExperimentsStorage()
    const storedVariant = experimentStorage.get([ctx.experimentName])
    // allows to manually override variant for testing purposes
    if (!isNil(storedVariant)) {
      return storedVariant
    }

    // 50% of guests will get 0 variant
    const variant = createWeightedBucket(2, 1, 1)

    experimentStorage.set(ctx.experimentName, variant)
    return variant
  },
  getTreatment(variant) {
    return variant
  }
}

/**********************************************
Experiment Name: Combined menu groups.
Description: 50% of the users will be presented with a navbar that contains all the menu groups. Menu selector will be hidden.
Variants:
  0: Control - No Treatment - 50%
  1: Combined all menu groups inside of the horizontal nav bar and hide menu selector.
**********************************************/
export const XP_COMBINED_MENU_GROUPS = {
  experimentName: 'xp-combined-menu-groups',
  flagName: FF.COMBINED_MENU_GROUPS,
  useInitVariant() {
    const ctx = XP_COMBINED_MENU_GROUPS
    const experimentStorage = getExperimentsStorage()
    const storedVariant = experimentStorage.get([ctx.experimentName])
    // allows to manually override variant for testing purposes
    if (!isNil(storedVariant)) {
      return storedVariant
    }
    // 50% of guests will get 0 variant
    const variant = createWeightedBucket(1, 1)
    experimentStorage.set(ctx.experimentName, variant)
    return variant
  },
  getTreatment(variant) {
    return variant
  }
}

/**********************************************
Experiment Name: Popular Items.
Description: Users will see different presentations or no presentation of popular items
Variants:
  0: Control - No Treatment - 33.33%
  1: Original popular items display/treatment.
  2: Carousel for popular items.
**********************************************/
export const XP_POPULAR_ITEMS = {
  experimentName: 'xp-popular-items',
  flagName: FF.POPULAR_ITEMS,
  useInitVariant() {
    const ctx = XP_POPULAR_ITEMS
    const experimentStorage = getExperimentsStorage()
    const storedVariant = experimentStorage.get([ctx.experimentName])
    // allows to manually override variant for testing purposes
    if (!isNil(storedVariant)) {
      return storedVariant
    }
    // each variant has a 33.33% chance of applying
    const variant = createWeightedBucket(1, 1, 1)
    experimentStorage.set(ctx.experimentName, variant)
    return variant
  },
  getTreatment(variant) {
    return variant
  }
}

// Basic port of Java's String.hashCode. Not particularly tuned for handling collisions,
// which in this case is not too important
function hashCode(s) {
  for (var i = 0, h = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}
